<template>
  <div>
    <trip-app-bar :trip="trip" />

    <v-container
      fluid
      :grid-list-lg="$vuetify.breakpoint.mdAndUp"
      class="py-0 py-sm-3"
    >
      <v-layout
        row
        wrap
      >
        <v-flex
          xs12
          md6
        >
          <v-card
            :tile="$vuetify.breakpoint.smAndDown"
            :flat="$vuetify.breakpoint.smAndDown"
            style="height: calc(100vh - 94px);display:flex;flex-direction:column;"
          >
            <div
              ref="map"
              class="google-map"
            />
          </v-card>
        </v-flex>

        <v-flex
          xs12
          md6
        >
          <v-card
            :tile="$vuetify.breakpoint.smAndDown"
            :flat="$vuetify.breakpoint.smAndDown"
          >
            <v-toolbar
              flat
              color="primary"
              dark
              class="trip-steps__toolbar"
            >
              <v-toolbar-title>
                <span class="d-none d-sm-inline">Duration </span>
                <strong>{{ tripDuration || '0 days' }}</strong>
              </v-toolbar-title>

              <v-toolbar-title class="ml-4">
                <span class="d-none d-sm-inline">Cost </span>
                <strong>€ {{ trip.cost || 0 }}</strong>
              </v-toolbar-title>

              <v-spacer />

              <dialog-travelers
                v-if="!trip.isNew()"
                :trip="trip"
              />

              <vu-file-upload
                v-if="canEdit"
                accept="image/*"
                type="button"
                :storage-path="`/${trip.id}`"
                storage-name="__image"
                :icon="trip.image ? 'mdi-image-edit' : 'mdi-image-plus'"
                @uploaded="onImageUploaded"
              />

              <v-btn
                v-if="canEdit"
                icon
                :to="{ name: 'trip-edit', params: { id: tripId } }"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text
              class="trip-steps__container py-0"
            >
              <v-timeline
                v-if="steps.length"
                dense
              >
                <template v-for="(step, i) in steps.models">
                  <v-timeline-item
                    :key="`step-${i}`"
                    :color="new Date(step.$.startDate) < new Date() ? 'success' : 'primary'"
                    :icon="getStepIcon(step)"
                    fill-dot
                  >
                    <v-layout
                      class="trip-step"
                      @click="canEdit ? openDialog(step) : null"
                    >
                      <v-flex xs4>
                        <div>{{ step.$.startDate | fmtDate }}</div>
                        <div>{{ step.$.endDate | fmtDate }}</div>
                        <div v-if="step.$.endDate">
                          <v-chip
                            small
                            outlined
                            label
                          >
                            {{ distanceInWordsStrict(step.$.startDate, step.$.endDate, { unit: 'd' }) }}
                          </v-chip>
                        </div>
                      </v-flex>

                      <v-flex>
                        <strong>{{ step.$.name }}</strong>
                        <div
                          v-if="step.$.location"
                        >
                          <v-icon small>
                            mdi-map-marker
                          </v-icon>
                          {{ step.$.location }}
                        </div>
                      </v-flex>

                      <v-flex
                        xs4
                        class="text-xs-right"
                      >
                        <div
                          v-if="step.$.cost"
                        >
                          € {{ step.$.cost }}
                        </div>

                        <div v-if="step.$.url">
                          <v-btn
                            outlined
                            x-small
                            :href="(step.$.url.includes('://') ? '' : 'https://') + step.$.url"
                            target="_blank"
                            @click.stop
                          >
                            <v-icon left>
                              mdi-link
                            </v-icon>
                            www
                          </v-btn>
                        </div>

                        <div v-if="step.$.attachment">
                          <v-btn
                            icon
                            :href="step.$.attachment"
                            target="_blank"
                            @click.stop
                          >
                            <v-icon>mdi-paperclip</v-icon>
                          </v-btn>
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-timeline-item>
                </template>
              </v-timeline>

              <div
                v-else
                class="pa-6"
              >
                <p class="display-1 grey--text text-xs-center font-weight-light">
                  Your trip is empty!
                </p>

                <p>
                  A trip is composed by many steps: they will appear here, once
                  you create them.
                </p>

                <p>
                  Steps are anything that you plan to do on your trip,
                  from airplane tavels to bus rides, from hotel accommodations
                  to events.
                </p>

                <v-btn
                  color="accent"
                  block
                  outlined
                  @click="openDialog()"
                >
                  <v-icon left>
                    mdi-shoe-print
                  </v-icon>
                  Add the first step
                </v-btn>
              </div>

              <div
                v-if="canEdit"
                class="trip-steps__footer"
              >
                <v-btn
                  fab
                  color="accent"
                  @click="openDialog()"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <trip-step-form
          :trip="trip"
          :edit-model="editStep"
          :show.sync="showEditDialog"
          @save="onStepSave"
        />
      </v-layout>
    </v-container>

    <v-overlay :value="!!error">
      <div>Sorry, trip not found or you don't have the privileges</div>
    </v-overlay>
  </div>
</template>

<script>
import { distanceInWordsStrict, format, min, max } from 'date-fns'

import TripStepForm from '@/components/TripStepForm'
import { getStepIcon } from '@/components/TripStepTypes'
import DialogTravelers from '@/components/DialogTravelers'
import TripAppBar from '@/components/TripAppBar'
import mapStyle from '@/components/mapStyle'
import VuFileUpload from '@/components/VuFileUpload'
import Trip from '@/models/Trip'
import TripStep from '@/models/TripStep'
import TripSteps from '@/models/TripSteps'
import { firestore } from '@/plugins/firebase'

export default {
  name: 'Trip',
  components: {
    DialogTravelers,
    TripAppBar,
    TripStepForm,
    VuFileUpload
  },
  data () {
    return {
      editStep: new TripStep(),
      error: null,
      map: null,
      mapMarkers: [],
      showEditDialog: false,
      steps: new TripSteps(),
      trip: new Trip()
    }
  },
  computed: {
    tripId () {
      return this.$route.params.id
    },
    tripDuration () {
      return (this.trip.startDate && this.trip.endDate)
        ? distanceInWordsStrict(this.trip.startDate, this.trip.endDate, { unit: 'd' }) : null
    },
    tripRef () {
      return this.trip.id ? firestore.collection('trips').doc(this.trip.id) : null
    },
    canEdit () {
      if (this.$currentUser) {
        const role = this.trip.users[this.$currentUser.id]
        return role === 'owner' || role === 'editor'
      } else {
        return false
      }
    }
  },
  async mounted () {
    await this.init()
    this.$store.commit('navigation/setToolbar', {
      image: this.trip.image
        ? this.trip.image : require('@/assets/images/trip-placeholder.jpg'),
      title: this.trip.name
    })
  },
  beforeDestroy () {
    this.$store.commit('navigation/setToolbar')
  },
  methods: {
    distanceInWordsStrict,
    getStepIcon,
    async init () {
      this.trip = new Trip()
      try {
        await this.trip.fetch(this.tripId)
        this.generateMetaTags()
      } catch (e) {
        if (e.code === 'permission-denied') {
          // this.$router.push('login')
          this.error = 'not-allowed'
          return
        }
      }

      /* global google */
      // eslint-disable-next-line no-new
      this.map = new google.maps.Map(this.$refs.map, {
        zoom: 8,
        disableDefaultUI: true,
        styles: mapStyle
      })

      this.steps = new TripSteps()
      this.steps.on('fetch', this.onStepsUpdate)
      await this.steps.fetch({
        where: [ [ 'tripId', '==', this.tripId ] ],
        orderBy: [
          [ 'startDate', 'asc' ],
          [ 'endDate', 'asc' ]
        ]
      })
    },
    async onStepsUpdate () {
      if (!this.steps.length) { return }

      // calculate start and end of trip
      let allDates = []
      for (const s of this.steps.models) {
        if (s.startDate) { allDates.push(s.startDate) }
        if (s.endDate) { allDates.push(s.endDate) }
      }

      this.trip.startDate = format(min(...allDates), 'YYYY-MM-DD')
      this.trip.endDate = format(max(...allDates), 'YYYY-MM-DD')

      // calculate total trip cost
      this.trip.cost = this.steps.reduce((acc, cur) => acc + (cur.cost || 0), 0)

      if (this.trip.changed()) {
        await this.save()
      }

      this.addMarkersToMap()
    },
    onStepSave () {
      if (this.editStep.country &&
          !this.trip.countries.includes(this.editStep.country)) {
        this.trip.countries.push(this.editStep.country)
        this.save()
      }
    },
    openDialog (step = new TripStep()) {
      this.editStep = step
      this.editStep.tripId = this.tripId
      this.showEditDialog = true
    },
    async save () {
      await this.trip.save()
    },
    async onImageUploaded (error, file, url) {
      if (error) {
        this.$notifyError(`Error saving trip image`)
        return
      }

      this.trip.image = url
      await this.save()

      this.$store.commit('navigation/setToolbar', {
        image: this.trip.image
          ? this.trip.image : require('@/assets/images/trip-placeholder.jpg'),
        title: this.trip.name
      })
    },
    addMarkersToMap (event) {
      if (this.steps.length) {
        const bounds = new google.maps.LatLngBounds()

        this.steps.each(step => {
          if (!step.$.location || !step.$.lat || !step.$.lng) { return }

          if (!this.mapMarkers[step.id]) {
            this.mapMarkers[step.id] = new google.maps.Marker({
              map: this.map
            })
          }

          const marker = this.mapMarkers[step.id]
          marker.setPosition({ lat: step.$.lat, lng: step.$.lng })
          marker.setLabel(step.$.name)

          const loc = new google.maps.LatLng(marker.position.lat(), marker.position.lng())
          bounds.extend(loc)
        })

        this.map.panToBounds(bounds)
        this.map.fitBounds(bounds)
        var listener = google.maps.event.addListener(this.map, 'idle', () => {
          if (this.map.getZoom() > 14) this.map.setZoom(14)
          google.maps.event.removeListener(listener)
        })
      }
    },
    generateMetaTags () {
      // <meta name="twitter:card" content="summary" />
      // <meta name="twitter:site" content="@nytimesbits" />
      // <meta name="twitter:creator" content="@nickbilton" />
      // <meta property="og:url" content="http://bits.blogs.nytimes.com/2011/12/08/a-twitter-for-my-sister/" />
      // <meta property="og:title" content="A Twitter for My Sister" />
      // <meta property="og:description" content="In the early days, Twitter grew so quickly that it was almost impossible to add new features because engineers spent their time trying to keep the rocket ship from stalling." />
      // <meta property="og:image" content="http://graphics8.nytimes.com/images/2011/12/08/technology/bits-newtwitter/bits-newtwitter-tmagArticle.jpg" />

      let tag

      tag = document.createElement('meta')
      tag.name = 'twitter:card'
      tag.content = 'summary'
      document.head.appendChild(tag)

      tag = document.createElement('meta')
      tag.name = 'og:title'
      tag.content = this.trip.name
      document.head.appendChild(tag)

      tag = document.createElement('meta')
      tag.name = 'og:url'
      tag.content = `https://vjago-app.web.app/trips/${this.trip.id}`
      document.head.appendChild(tag)

      if (this.trip.image) {
        tag = document.createElement('meta')
        tag.name = 'og:image'
        tag.content = this.trip.image
        document.head.appendChild(tag)
      }
    }
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.init()
  }
}
</script>

<style lang="scss">
@media (max-width: 600px) {
  .trip-steps__toolbar {
    position: sticky;
    top: 0;
    z-index: 7;
  }
}

@media (min-width: 601px) {
  .trip-steps__container {
    height: calc(100vh - 158px);
    overflow: auto;
  }
}

.trip-steps__footer {
  position: sticky;
  bottom: 16px;
  text-align: right;
  padding: 16px 0;
}

.trip-step {
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background-color: #f2f2f2;
  }
}

.contenteditable {
  background: none;
  border: none;
  width: 100%;
}

.google-map {
  flex: 1;
}
</style>
